import { lazy, type ComponentType } from "react";
import { hardReloadApp } from "@/lib/runtimeRecovery";

type Importer<T> = () => Promise<{ default: T }>;

/**
 * React.lazy with resilience for stale/failed chunk loads.
 *
 * Route chunks can fail to load when a new deploy invalidates the hashed file
 * names a returning visitor still has in memory, or on a flaky connection.
 * We retry twice with a short backoff, then fall back to the existing
 * hard-recovery path (clears SW + caches and reloads) instead of leaving the
 * user on a blank screen.
 */
export function lazyWithRetry<T extends ComponentType<any>>(importer: Importer<T>) {
  return lazy(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await importer();
      } catch (error) {
        if (attempt === 2) {
          void hardReloadApp("chunk-load");
          // Keep the promise pending while the reload happens so React does
          // not surface an error boundary flash.
          await new Promise(() => {});
        }
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
    // Unreachable, satisfies the type checker.
    throw new Error("Failed to load route chunk");
  });
}
